import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Filter, X, Globe, Lock, ShoppingBag, Loader2, RefreshCw } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { trpc } from '@/lib/api/trpc';
import { PluginCard } from '../plugin-card';
import { PluginDetails } from '../plugin-details-dialog';
import { DeployFromUrlDialog } from '../deploy-from-url-dialog';
import { ManagePreviewsDialog } from '../manage-previews-dialog';
import { usePluginInstallation } from '../installation/PluginInstallationContext';
import type { Plugin, PluginDeployment } from '../types';
import type { StepType } from '../../../storefront/types';

interface PluginBrowserProps {
  storeId: string;
  /** When true, only show public plugins */
  publicOnly?: boolean;
  /** When true, hide "My Plugins" tab */
  readOnly?: boolean;
  /** Pre-fill context (from step editor) */
  installationContext?: {
    funnelId?: string;
    stepType?: StepType;
  };
  /** Optional callback when user attempts to install while in read-only mode (trigger signup) */
  onInstallAttempt?: (plugin: Plugin, deploymentId?: string, preset?: string) => void;
}

/**
 * Simplified plugin browser component
 * Only handles: search, filter, display grid
 * Installation flow is handled by PluginInstallationOrchestrator
 */
export function PluginBrowser({
  storeId,
  publicOnly = false,
  readOnly = false,
  installationContext,
  onInstallAttempt,
}: PluginBrowserProps) {
  const { t } = useTranslation('storefront');
  const installation = usePluginInstallation();
  const { toast } = useToast();
  const utils = trpc.useUtils();

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [viewMode, setViewMode] = useState<'mine' | 'public'>(publicOnly || readOnly ? 'public' : 'public');
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  // Use nested layout instead of dialog
  const [selectedPluginId, setSelectedPluginId] = useState<string | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedDeploymentForPreviews, setSelectedDeploymentForPreviews] = useState<
    (PluginDeployment & { pluginName: string; storeId: string }) | null
  >(null);

  // Determine if we should show private content (not allowed in publicOnly or readOnly mode)
  const showPrivateContent = !publicOnly && !readOnly;

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch plugins - use public endpoint when in public/read-only mode
  const usePublicEndpoint = readOnly || (publicOnly && viewMode === 'public');

  // Public endpoint (no auth required)
  const {
    data: publicPluginsData,
    isLoading: isLoadingPublic,
    refetch: refetchPublicPlugins,
    isFetching: isFetchingPublic,
  } = trpc.plugins.listPublic.useQuery(
    {
      search: debouncedSearch || undefined,
      features: selectedFeatures.length > 0 ? selectedFeatures : undefined,
    },
    {
      enabled: usePublicEndpoint,
    },
  );

  // Protected endpoint (auth required)
  const {
    data: protectedPluginsData,
    isLoading: isLoadingProtected,
    refetch: refetchProtectedPlugins,
    isFetching: isFetchingProtected,
  } = trpc.plugins.list.useQuery(
    {
      storeId,
      publicOnly: viewMode === 'public' ? true : undefined,
      privateOnly: viewMode === 'mine' ? true : undefined,
      search: debouncedSearch || undefined,
      features: selectedFeatures.length > 0 ? selectedFeatures : undefined,
    },
    {
      enabled: !usePublicEndpoint && !!storeId,
    },
  );

  // Use the appropriate data source
  const pluginsData = usePublicEndpoint ? publicPluginsData : protectedPluginsData;
  const isLoading = usePublicEndpoint ? isLoadingPublic : isLoadingProtected;
  const isFetching = usePublicEndpoint ? isFetchingPublic : isFetchingProtected;
  const refetchPlugins = usePublicEndpoint ? refetchPublicPlugins : refetchProtectedPlugins;

  const plugins = (pluginsData?.plugins || []) as Plugin[];

  // Handle refresh
  const handleRefresh = async () => {
    await refetchPlugins();
    toast({
      title: t('plugins.browser.refreshed'),
      description: t('plugins.browser.templateListUpdated'),
    });
  };

  // Fetch available features for filtering - use public endpoint when in public/read-only mode
  const { data: publicFeaturesData } = trpc.plugins.getPublicAvailableFeatures.useQuery(
    {},
    { enabled: usePublicEndpoint },
  );

  const { data: protectedFeaturesData } = trpc.plugins.getAvailableFeatures.useQuery(
    {
      storeId,
      privateOnly: showPrivateContent && viewMode === 'mine' ? true : undefined,
      publicOnly: viewMode === 'public' ? true : undefined,
    },
    { enabled: !usePublicEndpoint && !!storeId },
  );

  const featuresData = usePublicEndpoint ? publicFeaturesData : protectedFeaturesData;
  const availableFeatures = featuresData?.features || [];

  // Request public listing mutation (replaces direct visibility toggle)
  const requestPublicListingMutation = trpc.plugins.requestPublicListing.useMutation({
    onSuccess: async (result) => {
      toast({
        title: t('plugins.browser.success'),
        description: result.message,
      });
      // Invalidate and refetch to show updated status
      await utils.plugins.list.invalidate();
      await utils.plugins.getDetails.invalidate();
    },
    onError: (error) => {
      toast({
        title: t('plugins.browser.error'),
        description: error.message || t('plugins.browser.failedUpdateVisibility'),
        variant: 'destructive',
      });
    },
  });

  // Handle feature toggle
  const handleToggleFeature = (feature: string) => {
    setSelectedFeatures((prev) =>
      prev.includes(feature) ? prev.filter((f) => f !== feature) : [...prev, feature],
    );
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setSelectedFeatures([]);
  };

  // Handle viewing plugin details (nested layout)
  const handleViewDetails = (plugin: Plugin) => {
    setSelectedPluginId(plugin.id);
  };

  // Handle back from details view
  const handleBackFromDetails = () => {
    setSelectedPluginId(null);
  };

  // Handle install from details view
  const handleInstallFromDetails = (deploymentId?: string, selectedPreset?: string) => {
    const plugin = plugins.find((p) => p.id === selectedPluginId);
    if (plugin) {
      handleInstall(plugin, deploymentId, selectedPreset);
    }
  };

  // Handle install click
  const handleInstall = (plugin: Plugin, deploymentId?: string, selectedPreset?: string) => {
    // If read-only mode (public marketplace), trigger callback for signup flow
    if (readOnly && onInstallAttempt) {
      onInstallAttempt(plugin, deploymentId, selectedPreset);
      return;
    }

    // Otherwise, start normal installation flow
    installation.startInstallation(plugin, deploymentId, installationContext, selectedPreset);
  };

  // Handle toggle visibility (request/cancel public listing)
  const handleToggleVisibility = (
    deploymentId: string,
    currentPublic: boolean,
    currentRequested: boolean,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation(); // Prevent card click

    // If already public (admin approved), do nothing - admin must revoke
    if (currentPublic) {
      toast({
        title: t('plugins.browser.info'),
        description: t('plugins.browser.publicAdminOnly'),
      });
      return;
    }

    // Toggle request status: if currently requested, cancel it; otherwise, request it
    requestPublicListingMutation.mutate({
      deploymentId,
      requested: !currentRequested,
    });
  };

  // Handle manage previews
  const handleManagePreviews = (
    deployment: PluginDeployment,
    pluginName: string,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    setSelectedDeploymentForPreviews({ ...deployment, pluginName, storeId });
    setPreviewDialogOpen(true);
  };

  // Show plugin details in nested layout when a plugin is selected (no padding wrapper)
  if (selectedPluginId) {
    return (
      <PluginDetails
        pluginId={selectedPluginId}
        storeId={storeId}
        viewMode={viewMode}
        onInstall={handleInstallFromDetails}
        onToggleVisibility={showPrivateContent && viewMode === 'mine' ? handleToggleVisibility : undefined}
        onManagePreviews={showPrivateContent && viewMode === 'mine' ? handleManagePreviews : undefined}
        readOnly={readOnly}
        onBack={handleBackFromDetails}
      />
    );
  }

  return (
    <div
      className={`flex flex-col ${publicOnly || readOnly ? 'min-h-0' : 'h-full min-h-0 overflow-y-auto'}`}
    >
      <div className="flex flex-col gap-4 p-5">
        {/* Header row: search + tabs + actions */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
            <Input
              placeholder={t('plugins.browser.searchTemplates')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 pl-9 text-sm"
            />
          </div>

          {!publicOnly && !readOnly && (
            <Tabs
              value={viewMode}
              onValueChange={(v) => setViewMode(v as 'mine' | 'public')}
              className="w-auto"
            >
              <TabsList className="h-9">
                <TabsTrigger value="public" className="gap-1.5 px-3 text-xs">
                  <Globe className="h-3.5 w-3.5" />
                  {t('plugins.browser.store')}
                </TabsTrigger>
                <TabsTrigger value="mine" className="gap-1.5 px-3 text-xs">
                  <Lock className="h-3.5 w-3.5" />
                  {t('plugins.browser.myTemplates')}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          {!publicOnly && !readOnly && viewMode === 'mine' && <DeployFromUrlDialog storeId={storeId} />}

          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            disabled={isFetching}
            title="Refresh"
            className="h-9 w-9 shrink-0"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Inline feature filter pills */}
        {availableFeatures.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <Filter className="text-muted-foreground mr-0.5 h-3.5 w-3.5" />
            {availableFeatures.map(({ feature, count }: { feature: string; count: number }) => (
              <button
                key={feature}
                type="button"
                onClick={() => handleToggleFeature(feature)}
                className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                  selectedFeatures.includes(feature)
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-transparent bg-muted/60 text-muted-foreground hover:bg-muted'
                }`}
              >
                {feature}
                <span className="ml-1 opacity-50">{count}</span>
              </button>
            ))}
            {selectedFeatures.length > 0 && (
              <button
                type="button"
                onClick={handleClearFilters}
                className="text-muted-foreground hover:text-foreground ml-1 inline-flex items-center gap-0.5 text-xs"
              >
                <X className="h-3 w-3" />
                {t('plugins.browser.clear')}
              </button>
            )}
          </div>
        )}

        {/* Results count */}
        {!isLoading && plugins.length > 0 && (
          <div className="text-muted-foreground flex items-center gap-2 text-xs">
            <span>
              {t('plugins.browser.templatesCount', { count: plugins.length })}
              {searchQuery && ` ${t('plugins.browser.found')}`}
            </span>
            {viewMode === 'mine' && !publicOnly && !readOnly && (
              <span className="text-muted-foreground/60">· {t('plugins.browser.private')}</span>
            )}
          </div>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="flex min-h-[300px] items-center justify-center">
            <div className="text-center">
              <Loader2 className="text-muted-foreground mx-auto h-8 w-8 animate-spin" />
              <p className="text-muted-foreground mt-3 text-sm">{t('plugins.browser.loadingTemplates')}</p>
            </div>
          </div>
        )}

        {/* Grid */}
        {!isLoading && plugins.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {plugins.map((plugin) => (
              <PluginCard
                key={plugin.id}
                plugin={plugin}
                onViewDetails={() => handleViewDetails(plugin)}
                viewMode={viewMode}
              />
            ))}
          </div>
        )}

        {/* Empty */}
        {!isLoading && plugins.length === 0 && (
          <div className="flex min-h-[260px] items-center justify-center rounded-xl border border-dashed">
            <div className="max-w-sm px-4 text-center">
              <ShoppingBag className="text-muted-foreground/40 mx-auto mb-3 h-10 w-10" />
              <p className="text-sm font-medium">{t('plugins.browser.noTemplatesFound')}</p>
              <p className="text-muted-foreground mt-1 text-xs">
                {searchQuery
                  ? t('plugins.browser.noResultsFor', { query: searchQuery })
                  : selectedFeatures.length > 0
                    ? t('plugins.browser.removeFilters')
                    : viewMode === 'public'
                      ? t('plugins.browser.newTemplatesRegularly')
                      : t('plugins.browser.deployFirstTemplate')}
              </p>
            </div>
          </div>
        )}

        {/* Manage Previews Dialog */}
        {selectedDeploymentForPreviews && (
          <ManagePreviewsDialog
            open={previewDialogOpen}
            onOpenChange={setPreviewDialogOpen}
            deploymentId={selectedDeploymentForPreviews.deploymentId}
            pluginName={selectedDeploymentForPreviews.pluginName}
            version={selectedDeploymentForPreviews.version}
            storeId={selectedDeploymentForPreviews.storeId}
          />
        )}
      </div>
    </div>
  );
}
